// IndexedDB (via Dexie) is the source of truth (idea.md section 4a). The app is
// fully usable offline with zero network: all logging, last-session targets, and
// the deterministic engine read from here. Notion sync is a background mirror.

import Dexie, { type Table } from 'dexie';
import type {
  Exercise,
  Routine,
  Session,
  BodyMeasurement,
  OutboxItem,
  MeasurementOutboxItem,
} from '../domain/types';
import { migrateLegacyMeasurement } from './migrateMeasurement';
import { DEFAULT_GYM_ID } from '../domain/gym';

export interface MetaRow {
  key: string;
  value: string;
}

export class GymDB extends Dexie {
  exercises!: Table<Exercise, string>;
  routines!: Table<Routine, string>;
  sessions!: Table<Session, string>;
  measurements!: Table<BodyMeasurement, string>;
  outbox!: Table<OutboxItem, string>;
  measurementOutbox!: Table<MeasurementOutboxItem, string>;
  meta!: Table<MetaRow, string>;

  constructor(name = 'gym-tracker') {
    super(name);
    this.version(1).stores({
      exercises: 'id, muscleGroup, archived',
      routines: 'id, weekday',
      sessions: 'id, date, routineId, coachedAt',
      measurements: 'id, date',
      outbox: 'sessionId, status',
      meta: 'key',
    });
    // v2: rich body-measurement schema (per-bodypart, relax/flex, L/R) + its own
    // sync outbox. Existing thin records are mapped to the new shape in-place.
    this.version(2)
      .stores({
        measurementOutbox: 'measurementId, status',
      })
      .upgrade(async (tx) => {
        await tx.table('measurements').toCollection().modify((m) => {
          const migrated = migrateLegacyMeasurement(m);
          for (const k of Object.keys(m)) delete (m as Record<string, unknown>)[k];
          Object.assign(m, migrated);
        });
      });
    // v3: fix the flat-vs-incline mislog. Flat DB press was logged under
    // 'incline-db-press' whenever it was substituted in for the Bench machine
    // (Upper A has no flat-bench DB exercise before this release). Re-key those
    // entries to the new 'flat-db-press' id so incline history is restored and the
    // flat work lands on its own exercise. Corrected finished sessions are
    // re-enqueued so the Notion mirror updates on next sync.
    this.version(3).upgrade(async (tx) => {
      const now = new Date().toISOString();
      const touched: string[] = [];
      await tx.table('sessions').toCollection().modify((s: Session) => {
        let changed = false;
        for (const entry of s.entries ?? []) {
          if (entry.exerciseId === 'incline-db-press' && entry.substitutedFrom === 'bench-machine') {
            entry.exerciseId = 'flat-db-press';
            changed = true;
          }
        }
        if (changed && s.finishedAt) touched.push(s.id);
      });
      for (const sessionId of touched) {
        await tx.table('outbox').put({ sessionId, status: 'pending', attempts: 0, lastError: null, updatedAt: now });
      }
    });
    // v4: sessions become gym-scoped. Every existing session predates the gym
    // picker, and all but a handful happened at the home gym, so backfill to FitX
    // rather than leaving them null (null history is invisible to gym-scoped
    // targets, which would blank out every "beat this" on upgrade). The few
    // Fitness First sessions get corrected by hand from the session screen; the
    // gymId index makes that a cheap lookup afterwards.
    this.version(4)
      .stores({
        sessions: 'id, date, routineId, coachedAt, gymId',
      })
      .upgrade(async (tx) => {
        await tx.table('sessions').toCollection().modify((s: Session) => {
          if (s.gymId === undefined) s.gymId = DEFAULT_GYM_ID;
        });
      });
  }
}

export const db = new GymDB();
